import { useContext, useEffect, useRef, useState } from "react";
import { SmartDPMapMarker } from "app/pages/SmartDP/Map/SmartDPMapMarker";
import useSupercluster from "use-supercluster";
import classNames from "classnames";
import GoogleMap from "google-map-react";
import appGlobalsContext from "app/AppGlobalsContext";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import "./ProductsMap.scss";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { DEFAULT_MAP_ZOOM } from "app/constants";

const Marker = ({ children }) => children;

const ProductsMap = ({
	center = {},
	onMarkerClick = () => {},
	onMapClick = () => {},
	onDrag = () => {},
	onGoogleApiLoaded = () => {},
	points = [],
	itemSelected,
	zoom,
	onMapChange = () => {},
	maxZoom = 10,
	enableClustering = true,
}) => {
	const googleAPIKey = useSelector(state => state.envVars.GOOGLE_API_KEY);
	const { isRTL } = useContext(appGlobalsContext);

	const [bounds, setBounds] = useState(null);
	// Controlled zoom passed to GoogleMap: only changes when the parent requests a deliberate,
	// programmatic zoom (focus on a pin, page reset). Never fed by the map's own onChange,
	// otherwise google-map-react's componentDidUpdate would call map.setZoom() on every user
	// gesture and fight the native smooth zoom animation (FBR-4844).
	const [controlledZoom, setControlledZoom] = useState(zoom);
	// Live zoom, for clustering only: updated directly from the map's own onChange, never fed
	// back into GoogleMap's zoom prop.
	const [liveZoom, setLiveZoom] = useState(zoom);

	const mapRef = useRef();

	// Clustering is opt-out: the SmartDP map renders every point individually
	// (full price vignettes for the top products, mini markers for the rest).
	const { clusters, supercluster } = useSupercluster({
		points: enableClustering ? points : [],
		bounds,
		zoom: liveZoom,
		options: { radius: 75, maxZoom: maxZoom },
	});

	const clusterClassNames = leaves => {
		const names = leaves.map(leave => leave.properties.name);

		return classNames({
			"products-map__cluster-marker": true,
			"products-map__cluster-marker--active": names.includes(itemSelected),
		});
	};

	useEffect(() => {
		// mettre à jour le zoom si il change depuis l'extérieur (hover sur les products cards par exemple)
		setControlledZoom(zoom);
		setLiveZoom(zoom);
	}, [zoom]);

	if (!googleAPIKey) {
		return null;
	}

	return (
		<GoogleMap
			// false (the library default) keeps the map's actual current center on a
			// container resize; `true` would snap it back to the `center` prop, which
			// never tracks the user's manual pan (see comments below), causing the map
			// to jump back whenever the container resizes (e.g. on Windows, opening the
			// mini-search modal hides the classic scrollbar and resizes the container).
			resetBoundsOnResize={false}
			options={() => ({
				scrollwheel: true,
				clickableIcons: false,
				fullscreenControl: false,
				scaleControl: true,
				gestureHandling: "greedy",
				// Without a mapId (vector rendering), Google Maps defaults to integer-only zoom
				// levels: a scroll-wheel or pinch gesture snaps to the nearest step instead of
				// zooming continuously (FBR-4844).
				isFractionalZoomEnabled: true,
				zoomControl: true,
				zoomControlOptions: {
					position: isRTL
						? window?.google?.maps?.ControlPosition.LEFT_TOP
						: window?.google?.maps?.ControlPosition.RIGHT_TOP,
				},
			})}
			onClick={onMapClick}
			onDrag={onDrag}
			onChange={({ zoom, bounds }) => {
				onMapChange(zoom, bounds);
				setLiveZoom(zoom);
				setBounds([bounds.nw?.lng, bounds.se?.lat, bounds.se?.lng, bounds.nw?.lat]);
			}}
			bootstrapURLKeys={{
				key: googleAPIKey,
			}}
			hoverDistance={30}
			center={center}
			zoom={controlledZoom}
			defaultZoom={DEFAULT_MAP_ZOOM}
			yesIWantToUseGoogleMapApiInternals
			onGoogleApiLoaded={({ map }) => {
				mapRef.current = map;
				onGoogleApiLoaded({ map });
			}}
		>
			{!enableClustering
				? points.map(point => {
						const [longitude, latitude] = point.geometry.coordinates;
						const { name, productUrl, isFlashsale, total, tag, shape } =
							point.properties;

						return (
							<SmartDPMapMarker
								key={name}
								lat={latitude}
								lng={longitude}
								name={name}
								productUrl={productUrl}
								total={total}
								isFlashsale={isFlashsale}
								isActive={itemSelected === name}
								onMarkerClick={onMarkerClick}
								tag={tag}
								shape={shape}
							/>
						);
					})
				: clusters.map(cluster => {
						const [longitude, latitude] = cluster.geometry.coordinates;
						const {
							cluster: isCluster,
							point_count: pointCount,
							name,
							productUrl,
							isFlashsale,
							total,
							tag,
						} = cluster.properties;

						if (isCluster) {
							return (
								<Marker
									key={`cluster-${cluster.id}`}
									lat={latitude}
									lng={longitude}
								>
									<div
										className={clusterClassNames(
											supercluster.getLeaves(cluster.id, Infinity)
										)}
										style={{
											width: `${10 + (pointCount / points.length) * 20}px`,
											height: `${10 + (pointCount / points.length) * 20}px`,
										}}
										onClick={e => {
											e.stopPropagation();
											if (mapRef?.current) {
												const expansionZoom = Math.min(
													supercluster.getClusterExpansionZoom(
														cluster.id
													),
													20
												);
												mapRef.current.setZoom(expansionZoom);
												mapRef.current.panTo({
													lat: latitude,
													lng: longitude,
												});
											}
										}}
									>
										<Typography variant={TYPOGRAPHY_VARIANTS.SMALL} isBold>
											{pointCount}
										</Typography>
									</div>
								</Marker>
							);
						}
						return (
							<SmartDPMapMarker
								key={name}
								lat={latitude}
								lng={longitude}
								name={name}
								productUrl={productUrl}
								total={total}
								isFlashsale={isFlashsale}
								isActive={itemSelected === name}
								onMarkerClick={onMarkerClick}
								tag={tag}
							/>
						);
					})}
		</GoogleMap>
	);
};

ProductsMap.propTypes = {
	center: PropTypes.shape({
		lat: PropTypes.number,
		lng: PropTypes.number,
	}),
	onMarkerClick: PropTypes.func,
	onMapClick: PropTypes.func,
	onDrag: PropTypes.func,
	onGoogleApiLoaded: PropTypes.func,
	// onMapChange is called with (zoom, bounds) on every map move/zoom
	onMapChange: PropTypes.func,
	points: PropTypes.array,
	itemSelected: PropTypes.string,
	zoom: PropTypes.number,
	maxZoom: PropTypes.number,
	enableClustering: PropTypes.bool,
};

export default ProductsMap;
